import axios from 'axios';
import env from '@/config/env';

/**
 * Client HTTP public (sans JWT) pour les endpoints de paiement.
 * Utilisé par les pages /payment/:token accessibles sans authentification.
 */
const publicAxios = axios.create({
  baseURL: env.PUBLIC_API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

export interface PaymentTokenMetadata {
  vehicleBrand?: string;
  vehicleModel?: string;
  monthlyAmount?: number;
  duration?: number;
  totalAmount?: number;
  serviceFees?: number;
  sellingPrice?: number;
  companyName?: string;
  companyLogo?: string;
  companyCode?: string;
}

/** Identité du garant affichée sur la page de paiement (EVO-05). */
export interface UpsellPaymentBranding {
  name: string;
  legalName: string;
  /** URL absolue du logo. */
  logoUrl: string | null;
  /** Couleurs #RRGGBB, couleurs de texte lisibles fournies par l'API. */
  primaryColor: string;
  accentColor: string;
  primaryTextColor: string;
  accentTextColor: string;
  address: string | null;
  cp: string | null;
  city: string | null;
  siren: string | null;
}

/** Assistance du contrat : « non » explicite quand elle est absente. */
export interface UpsellPaymentAssistance {
  included: boolean;
  breakdown: boolean;
  loanerVehicle: boolean;
  loanerDays: number;
  label: string | null;
  benefits: Array<{ label: string; value: string }>;
}

/** Récapitulatif de la garantie souscrite (contrats upsell uniquement). */
export interface UpsellPaymentContractSummary {
  contractLabel: string | null;
  beneficiaryName: string | null;
  warrantyName: string | null;
  /** Durée en mois. */
  duration: number | null;
  kmLimit: number | null;
  /** Plafond financier en euros. */
  financialCap: number | null;
  /** Date ISO. */
  startDate: string | null;
  isLastChance: boolean;
  partsCount: number;
  partsByCategory: Array<{ category: string; parts: string[] }>;
  assistance: UpsellPaymentAssistance;
  retractionDays: number;
}

/** Affinicar, qui vend pour le compte du garant : contact donné au payeur. */
export interface UpsellPaymentPlatformContact {
  name: string;
  phone: string | null;
  email: string | null;
}

export interface UpsellPaymentSummary {
  branding: UpsellPaymentBranding;
  contact?: UpsellPaymentPlatformContact;
  summary: UpsellPaymentContractSummary;
}

export interface PaymentTokenStripeData {
  requiresSignature?: false;
  intentType: 'setup_intent' | 'payment_intent';
  clientSecret: string;
  stripeAccountId: string;
  paymentMethod: string | null;
  metadata: PaymentTokenMetadata | null;
  isDemo?: boolean;
  /** Présent pour un contrat upsell : identité du garant et récapitulatif (EVO-05). */
  upsell?: UpsellPaymentSummary | null;
}

/**
 * Renvoyé quand le contrat attend encore une signature électronique.
 * Le frontend doit rediriger le navigateur vers `signatureLink`.
 */
export interface PaymentTokenSignatureRequiredData {
  requiresSignature: true;
  signatureLink: string;
}

export type PaymentTokenData =
  | PaymentTokenStripeData
  | PaymentTokenSignatureRequiredData;

export const paymentApi = {
  /** Résout un token et retourne les données Stripe ou un ordre de redirection signature */
  resolveToken: async (token: string): Promise<PaymentTokenData> => {
    const { data } = await publicAxios.get<PaymentTokenData>(`/payment-tokens/${token}`);
    return data;
  },

  /** Marque un token comme utilisé après paiement réussi */
  completePayment: async (token: string): Promise<void> => {
    await publicAxios.post(`/payment-tokens/${token}/complete`);
  },
};
